import React, { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Input } from 'reactstrap'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { api } from "../../utils/variables"
import { POLL_INTERVAL_MS } from '../../config/syncConfig'
import axios from "axios"
import { usePlatforms } from '../../contexts/PlatformsContext'

import { apiService } from "../../utils/prefetchManager"
import { buildKeywordSearchParams, KEYWORD_SEARCH_PATH, normalizeSearchGames } from "../../utils/keywordSearch"

const GlobalSearchBar = ({ style, placeholder = "Search by name or paste a URL..." }) => {
    const { getActivePlatforms, getPlatformDisplayName, getPlatformIcon } = usePlatforms();
    const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200)
    const isMobile = windowWidth < 768
    const isTablet = windowWidth >= 768 && windowWidth < 1024
    const isSmallMobile = windowWidth < 480
    const isVerySmallMobile = windowWidth < 360
    const navigate = useNavigate()
    const [searchTerm, setSearchTerm] = useState("")
    const [searchResults, setSearchResults] = useState([])
    const [loading, setLoading] = useState(false)
    const [showDropdown, setShowDropdown] = useState(false)
    const searchRef = useRef(null)

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchTerm.trim()) {
                handleSearch()
            } else {
                setSearchResults([])
            }
        }, 500)

        return () => clearTimeout(delayDebounceFn)
    }, [searchTerm])

    // Track window width for responsive behavior
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth)
        }
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowDropdown(false)
            }
        }
        // Use both mousedown and touchstart for better mobile support
        document.addEventListener("mousedown", handleClickOutside)
        document.addEventListener("touchstart", handleClickOutside)
        return () => {
            document.removeEventListener("mousedown", handleClickOutside)
            document.removeEventListener("touchstart", handleClickOutside)
        }
    }, [])

    const isUpgradeError = error => {
        const response = error?.response
        const data = response?.data || {}
        const status = data?.status
        const action = data?.action || ""
        const current_tier = data?.current_tier || ""
        return status === 400 && action === "UPGRADE" && current_tier
    }

    const isUpgradeResponse = data => {
        if (!data || typeof data !== "object") return false
        const status = data?.status
        const action = data?.action || ""
        const current_tier = data?.current_tier || ""
        return status === 400 && action === "UPGRADE" && current_tier
    }

    const handleSearch = async () => {
        if (!searchTerm.trim()) return

        setLoading(true)
        setShowDropdown(true)

        const fetchPlatformResults = async (platformObj) => {
            const platform = platformObj.id;
            try {
                const params = new URLSearchParams(buildKeywordSearchParams(searchTerm, { page: 1, platform }))
                const url = `${api}${KEYWORD_SEARCH_PATH}?${params.toString()}`

                let response = await axios.get(url)

                if (isUpgradeResponse(response.data)) return { upgrade: true }

                let action = (response.data?.action || "").toLowerCase()
                let attempts = 0
                const maxAttempts = 12
                const pollIntervalMs = POLL_INTERVAL_MS || 5000

                while (action === 'loading' && attempts < maxAttempts) {
                    attempts++
                    await new Promise(r => setTimeout(r, pollIntervalMs))
                    response = await axios.get(url)
                    if (isUpgradeResponse(response.data)) return { upgrade: true }
                    action = (response.data?.action || "").toLowerCase()
                }

                const games = normalizeSearchGames(response.data?.games || []);
                const platformName = getPlatformDisplayName(platform) || platformObj.name || platform;
                const platformIconUrl = getPlatformIcon ? getPlatformIcon(platform) : null;
                
                return { 
                    games: games.map(game => ({
                        ...game,
                        platform: game.platform || platformName,
                        platformIcon: platformIconUrl
                    }))
                }
            } catch (error) {
                if (isUpgradeError(error)) return { upgrade: true }
                console.error(`Search failed for ${platformObj.id}:`, error)
                return { games: [] }
            }
        }

        try {
            // Get all active platforms and search across all of them
            const activePlatforms = getActivePlatforms();
            const platformPromises = activePlatforms.map(platform => 
                fetchPlatformResults(platform)
            );
            
            const platformResults = await Promise.all(platformPromises);

            // Check if any platform requires upgrade
            if (platformResults.some(result => result.upgrade)) {
                setSearchResults([])
                return
            }

            // Combine results from all platforms
            const allGames = platformResults.flatMap(result => result.games || []);
            setSearchResults(allGames)
        } catch (err) {
            console.error("Handle search error", err)
            setSearchResults([])
        } finally {
            setLoading(false)
        }
    }

    const handleResultClick = (game) => {
        setShowDropdown(false)
        navigate(`/xr/app?id=${game.item_id || game._id}`)
    }

    // Responsive styles
    const iconLeft = isVerySmallMobile ? '10px' : isSmallMobile ? '11px' : isMobile ? '12px' : '15px'
    const iconSize = isVerySmallMobile ? '11px' : isSmallMobile ? '12px' : isMobile ? '12px' : '14px'
    const inputPaddingLeft = isVerySmallMobile ? '30px' : isSmallMobile ? '32px' : isMobile ? '34px' : '45px'
    const inputFontSize = isVerySmallMobile ? '13px' : isSmallMobile ? '13px' : isMobile ? '14px' : (style?.fontSize || '14px')

    return (
        <div
            ref={searchRef}
            className="position-relative w-100"
            style={{
                maxWidth: isMobile ? "100%" : isTablet ? "550px" : "650px",
                height: style?.height || (isVerySmallMobile ? "32px" : isSmallMobile ? "34px" : isMobile ? "36px" : "44px"),
                background: "#21222D",
                border: "1px solid #353748",
                borderRadius: style?.borderRadius || (isMobile ? "20px" : "30px"),
                ...style
            }}
        >
            <i className="fa-solid fa-magnifying-glass" style={{
                position: 'absolute',
                left: iconLeft,
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#878a99',
                fontSize: iconSize,
                zIndex: 10,
                pointerEvents: 'none'
            }}></i>
            <Input
                type="text"
                className="form-control"
                placeholder={placeholder}
                aria-label="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                onFocus={() => {
                    if (searchTerm.trim()) setShowDropdown(true)
                }}
                style={{
                    height: "100%",
                    background: "transparent",
                    border: "none",
                    color: "#fff",
                    boxShadow: 'none',
                    paddingLeft: inputPaddingLeft,
                    paddingRight: isMobile ? (isVerySmallMobile ? '10px' : isSmallMobile ? '11px' : '12px') : '15px',
                    fontSize: inputFontSize,
                    // Improve touch targets on mobile
                    WebkitAppearance: 'none',
                    appearance: 'none'
                }}
            />
            {showDropdown && (searchTerm || loading) && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    width: '100%',
                    background: '#21222D',
                    border: '1px solid #353748',
                    borderRadius: isMobile ? '0 0 8px 8px' : '8px',
                    marginTop: '8px',
                    maxHeight: isMobile ? `${Math.min(window.innerHeight * 0.5, 300)}px` : '400px',
                    overflowY: 'auto',
                    zIndex: isMobile ? 10000 : 1000,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                    // Better scrolling on mobile
                    WebkitOverflowScrolling: 'touch',
                    overscrollBehavior: 'contain',
                    // Prevent horizontal scroll on mobile
                    overflowX: 'hidden'
                }}>
                    {loading ? (
                        <div className="p-2">
                            {Array(5).fill(0).map((_, i) => (
                                <div key={i} className="d-flex align-items-center p-2" style={{ borderBottom: '1px solid #2E3244' }}>
                                    <Skeleton width={40} height={40} borderRadius={6} baseColor="#2a2a35" highlightColor="#3e3e4e" style={{ marginRight: 12 }} />
                                    <div>
                                        <Skeleton width={120} height={16} baseColor="#2a2a35" highlightColor="#3e3e4e" style={{ marginBottom: 4 }} />
                                        <Skeleton width={60} height={12} baseColor="#2a2a35" highlightColor="#3e3e4e" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <>
                            {searchResults.length > 0 ? (
                                searchResults.map((game, index) => (
                                    <div
                                        key={game.item_id || index}
                                        onClick={() => handleResultClick(game)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: isMobile ? '12px' : '10px',
                                            cursor: 'pointer',
                                            borderBottom: '1px solid #2E3244',
                                            transition: 'background 0.2s',
                                            minHeight: isMobile ? '56px' : 'auto', // Better touch target
                                            WebkitTapHighlightColor: 'transparent',
                                            userSelect: 'none'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#2E3244'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                        onTouchStart={(e) => {
                                            e.currentTarget.style.background = '#2E3244'
                                        }}
                                        onTouchEnd={(e) => {
                                            setTimeout(() => {
                                                e.currentTarget.style.background = 'transparent'
                                            }, 150)
                                        }}
                                    >
                                        <img
                                            src={game.image_url}
                                            alt={game.name}
                                            style={{
                                                width: isMobile ? 44 : 40,
                                                height: isMobile ? 44 : 40,
                                                borderRadius: 6,
                                                marginRight: isMobile ? 12 : 12,
                                                objectFit: 'cover',
                                                flexShrink: 0
                                            }}
                                        />
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{
                                                color: '#fff',
                                                fontWeight: 500,
                                                fontSize: isMobile ? 15 : 14,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}>{game.name}</div>
                                            <div style={{
                                                color: '#878a99',
                                                fontSize: isMobile ? 13 : 12,
                                                marginTop: '4px',
                                                display: 'flex',
                                                alignItems: 'center'
                                            }}>
                                                {game.platformIcon ? (
                                                    <img 
                                                        src={game.platformIcon} 
                                                        alt={game.platform} 
                                                        style={{ width: 14, height: 14, opacity: 0.8 }} 
                                                    />
                                                ) : (
                                                    game.platform
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                searchTerm && (
                                    <div className="p-3 text-center" style={{
                                        color: '#878a99',
                                        fontSize: isMobile ? 14 : 13,
                                        padding: isMobile ? '16px' : '12px'
                                    }}>
                                        No results found
                                    </div>
                                )
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    )
}

export default GlobalSearchBar
